import React, { useContext, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { PlanSearch } from './screens/PlanSearch';
import { Entitlement } from './components/FileClaim/Entitlement';
import { Payment } from './components/FileClaim/Payment';
import { VerifyInfo } from './components/FileClaim/VerifyInfo';
import { Reimbursement } from './components/FileClaim/Reimbursement';
import { PlanOverview } from './screens/PlanOverview/PlanOverview';
import { ContractContext } from './components/contexts/ContractContext';
import { ContractsOverview } from './screens/PlanOverview/ContractsOverview';
import { Confirmation } from './components/FileClaim/Confirmation';
import Landing from './screens/Vendor/Apple/Landing';
import { CoverageDocsCostco } from './screens/Vendor/Apple/CoverageDocsCostco';
import { ClaimOverview } from './screens/Vendor/Apple/ClaimOverview';
import { DetailsOverview } from './screens/PlanOverview/DetailsOverview';
import AssetCoverageOverview from './components/PlanOverview/Asset/AssetCoverageOverview';
import EntitlementContainer from './screens/Vendor/Apple/EntitlementContainer';
import VerifyInfoContainer from './screens/Vendor/Apple/VerifyInfoContainer';
import PaymentContainer from './screens/Vendor/Apple/PaymentContainer';
import { ConfirmationLT } from './components/Vendor/Apple/IntakeLoss/ConfirmationLT';
import ErrorMessages from './components/Vendor/Apple/IntakeLoss/ErrorMessages';
import '../src/css/autodropdown.css';
import { UploadDocsContainer } from './screens/Vendor/Apple/UploadDocsContainer';
import Faqs from './screens/Vendor/Apple/Faqs';
import { Helmet } from 'react-helmet';
import HWAFavicon from './assets/images/Vendor/HWA/favicon.ico';
import AIGFavicon from './assets/images/aigfavicon.ico';
import WISHFavicon from './assets/images/WISHFavicon.ico';
import WISHFavicon16 from './assets/images/WISHFavicon-16x16.png';
import WISHFavicon32 from './assets/images/WISHFavicon-32x32.png';
import WISHAndroidFavicon192 from './assets/images/WISH-android-chrome-192x192.png';
import WISHAndroidFavicon512 from './assets/images/WISH-android-chrome-512x512.png';
import WISHAppleTouchFavicon from './assets/images/WISH-apple-touch-icon.png';
import ETFavicon from './assets/images/Vendor/ET/favicon.ico';
import { EConsent } from './screens/Vendor/Apple/EConsent';
import { ProgramSummary } from './screens/Vendor/Apple/ProgramSummary';
import PlanType from './components/Vendor/Apple/FileInfo/PlanType';
import ContactUs from './screens/Vendor/Apple/ContactUs';
import { ContactConfirmation } from './components/Vendor/Apple/Contact/ContactConfirmation';
import { ETContactConfirmation } from './components/Vendor/ET/Contact/ContactConfirmation';
import { ApptsWrapper } from './components/FileClaim/Appointments';
import RedirectComponent from './screens/Vendor/Apple/RedirectComponent';
import { AltServiceOptions } from './components/AltServiceOptions/AltServiceOptions';
import { HeaderController } from './components/Headers/HeaderController';
import { FooterController } from './components/Footers/FooterController';
import { CoverageDocsController } from './components/CoverageDocs/CoverageDocsController';
import qs from 'qs';
import {
    verifyHwaToken,
    verifyAigToken,
    verifyAigJwtToken,
    getHwaRedirectUrl,
    isAppleRoute,
    getFeatureFlags
} from '../src/helpers';
import FAQ from './components/FAQ/FAQ';
import CustomerInfo from './components/Sales/CustomerInfo';
import CustomerSurvey from './components/Vendor/Apple/CustomerSurvey/CustomerSurvey';
import ChatEnd from './components/Vendor/Hwa/ChatEnd';
import { PlanLookup } from './components/PlanSearch/PlanLookup';
import VerifyAigToken from './components/Vendor/Hwa/VerifyAigToken';
import ViewPlanDetails from './screens/PlanOverview/ViewPlanDetails';
import CustomerDetails from './components/Vendor/ET/CustomerDetails';
import DevelopmentBrandToggler from './components/_BrandingProvider/DevelopmentBrandToggler';
import { useEnvironment } from './components/_ReactHooks/useEnvironment';
import { Providers } from './Providers';
import PdfViewer from './components/Global/PdfViewer';
import ResumeClaimSubmission from './screens/Vendor/Apple/ResumeClaimSubmission';
import ForgotPlanNumber from './components/PlanSearch/ForgotPlanNumber';
import AccountVerification from './components/PlanSearch/AccountVerification';
import { IdleTimerProvider, useIdleTimerContext } from 'react-idle-timer';
import HandleTimeoutModal from './SessionTimeout/HandleTimeoutModal';
import ViewAllAccounts from './components/PlanSearch/ViewAllAccounts';
import CreateNewPassword from './components/PlanSearch/CreateNewPassword';
import ForgotPassword from './components/PlanSearch/ForgotPassword';
import AppleCoverageDocs from './screens/Vendor/Apple/CoverageDocs';
import SystemErrorMessage from './components/shared/SystemErrorMessage';
import { useLogout } from './components/shared/logout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { MaintenancePage } from './screens/MaintenancePage';
import ReimbursementForm from './screens/Forms/ReimbursmentForm';
import ReimbursementConfirmation from './components/FileClaim/ReimbursementConfirmation';

export default function App() {
    const { isDevOrLower, isAnyNonProduction, isStagingOrLower, showBrandButtons, enforceToken, errorPath, loginPath } =
        useEnvironment();
    const location = useLocation();
    const history = useHistory();
    const params = useParams();
    let claim = location?.state?.claim;
    const { data = {} } = useContext(ContractContext) || {};
    const { contract: { clientId = 'aig' } = {} } = !data || data === null ? {} : data;
    const [loading, setLoading] = useState(true);
    const [isCancelClaimFlow, setIsCancelClaimFlow] = useState(false);
    const [showIdleModal, setShowIdleModal] = useState(false);
    const [clientIdByURL, setClientIdByURL] = useState('aig');
    const isApple = clientIdByURL === 'apple';
    const { handleLogout } = useLogout();

    // Fired on every route change
    useEffect(() => {
        //These paths already include token checks, and this limits token API runs
        const paths = [
            '/faq',
            '/planoverview',
            '/vendor/VerifyAigToken',
            '/assetcoverageoverview',
            '/coveragedocs',
            '/info/contact-us',
            '/RequestReimbursementForm',
            '/reimbursementform'
        ];

        const requiresTokenValidation = !paths.some((path) => location.pathname.includes(path));

        if (requiresTokenValidation) {
            checkAndVerifyToken();
        }
    }, [location.pathname]);

    //Suspend component rendering until environment/client variables have been pulled, race condition fixes
    useEffect(() => {
        const getClientByURL = async () => {
            await fetch('api/helper/getClientByUrl', {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then((res) => res.json())
                .then((envRes) => {
                    const clientId = envRes?.clientId?.toLowerCase();
                    setClientIdByURL(clientId);
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => setLoading(false));
        };

        const setEnvironmentFlags = async () => {
            const env = await getFeatureFlags();
            sessionStorage.setItem('featureFlags', JSON.stringify(env));
        };

        getClientByURL();
        setEnvironmentFlags();
    }, []);

    const checkAndVerifyToken = async () => {
        const appSession = sessionStorage.getItem('appState');
        const sessionClientToken = JSON.parse(appSession)?.clientToken;

        if (location.pathname.includes('hwa') || clientId == 'hwa') {
            const token = qs.parse(window.location.search, { ignoreQueryPrefix: true }).id;
            if (typeof token !== 'undefined' && token !== '' && token !== null) {
                // hwa, verify the token and redirect if invalid
                const isTokenValid = await verifyHwaToken(token);
                if (!isTokenValid) {
                    const url = await getHwaRedirectUrl();
                    window.location.href = `${url}/auth/login`;
                }
            }
        } else {
            const token =
                typeof params.token == 'undefined'
                    ? qs.parse(location?.search, { ignoreQueryPrefix: true })?.token || sessionClientToken
                    : params.token;
            const tokenResponse = await verifyAigJwtToken(token);
            let path = tokenResponse.redirect;

            if (token) {
                // verify the token and redirect if invalid
                const isTokenValid = await verifyAigToken(token);
                if (!isTokenValid) {
                    path += tokenResponse.loginPath;
                    handleRoute(path);
                }
            } else if (tokenResponse && tokenResponse.enforceToken) {
                path += tokenResponse.errorPath;
                handleRoute(path);
            }
        }
    };

    const handleRoute = (path) => {
        if (!path) return;

        if (path.includes('http')) {
            window.location.href = path;
        } else {
            history.push({ pathname: path });
        }
    };

    const handleSkip = (e) => {
        e.preventDefault();
        document.getElementById('reach-skip-nav').scrollIntoView({
            behavior: 'smooth'
        });
        document.getElementById('reach-skip-nav').focus();
    };

    const logoClickHandler = () => {
        if (window.location.pathname.toLowerCase().includes('fileclaim/entitlement')) {
            setIsCancelClaimFlow(true);
        } else {
            window.location = '/planoverview';
        }
    };

    const cancelClaimResumeHandler = () => {
        setIsCancelClaimFlow(false);
    };

    const onPrompt = () => {
        setShowIdleModal(true);
    };

    const onIdleLogout = () => {
        setShowIdleModal(false);
        handleLogout();
    };

    const handleContinue = () => {
        setShowIdleModal(false);
    };

    if (loading) {
        return null;
    }

    return (
        <IdleTimerProvider
            timeout={1000 * 60 * 15}
            onPrompt={onPrompt}
            promptBeforeIdle={1000 * 60 * 2}
            startManually={true}
            crossTab={true}
            syncTimers={200}
        >
            <Providers>
                <div
                    className='skip_nav'
                    role='banner'
                    aria-label='skip navigation'
                >
                    <a
                        href=''
                        onClick={handleSkip}
                        data-reach-skip-link
                        data-reach-skip-nav-link
                    >
                        Skip to Content
                    </a>
                </div>
                <HeaderController onLogoClickHandler={logoClickHandler} />
                {showIdleModal && (
                    <HandleTimeoutModal
                        showModal={showIdleModal}
                        onContinue={handleContinue}
                        onLogout={onIdleLogout}
                    />
                )}
                <main
                    role='main'
                    style={{ minHeight: 'calc(100vh - 235px)', position: 'relative' }}
                >
                    <Switch>
                        {clientIdByURL === 'applecostco' && (
                            <Route
                                exact
                                path='/'
                                component={CoverageDocsCostco}
                            />
                        )}
                        {window.location.href.toLowerCase().includes('aigtheftandloss')
                        || window.location.href.toLowerCase().includes('theftandloss') ? (
                            window.location.href.toLowerCase().includes('customersurvey') ? (
                                <Route
                                    exact
                                    path='/vendor/apple/customersurvey/:claimId'
                                    component={CustomerSurvey}
                                />
                            ) : (
                                <Route
                                    exact
                                    path='/'
                                    component={Landing}
                                />
                            )
                        ) : window.location.href.toLowerCase().includes('customersurvey') ? (
                            <Route
                                exact
                                path='/vendor/apple/customersurvey/:claimId'
                                component={CustomerSurvey}
                            />
                        ) : (
                            <Route
                                exact
                                path='/'
                                component={PlanSearch}
                            />
                        )}
                        {(!isApple || isStagingOrLower) && (
                            <Route
                                exact
                                path='/fileclaim/entitlement/:assetId/:claimId/:contractId/:coverageId'
                                render={(props) => (
                                    <Entitlement
                                        cancelClaim={isCancelClaimFlow}
                                        resumeClaim={cancelClaimResumeHandler}
                                        history={history}
                                        {...props}
                                        showTimeoutModal={showIdleModal}
                                    />
                                )}
                            />
                        )}
                        {(!isApple || isStagingOrLower) && (
                            <Route
                                exact
                                path='/appointment'
                                render={(props) => (
                                    <ApptsWrapper
                                        showTimeoutModal={showIdleModal}
                                        {...props}
                                    />
                                )}
                            />
                        )}
                        {/* RENDERED ROUTES FOR APPLE */}
                        <ProtectedRoute
                            exact
                            path='/vendor/apple'
                            component={Landing}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/resume/:token'
                            component={ResumeClaimSubmission}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/claimoverview'
                            component={ClaimOverview}
                            history={history}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/entitlement'
                            component={EntitlementContainer}
                            history={history}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/verifyinfo'
                            component={VerifyInfoContainer}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/payment'
                            component={PaymentContainer}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/confirmation'
                            component={ConfirmationLT}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/error'
                            component={ErrorMessages}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/FAQ'
                            component={Faqs}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/uploaddocs'
                            component={UploadDocsContainer}
                            history={history}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/eConsent'
                            component={EConsent}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/programsummarydisclosures'
                            component={ProgramSummary}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/coveragedocs'
                            component={AppleCoverageDocs}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/contactconfirmation'
                            component={ContactConfirmation}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/apple/customersurvey/:claimId'
                            component={CustomerSurvey}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/paynow/:token/:clientId'
                            component={RedirectComponent}
                            isValidRoute={isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/payment/callback/:paymentMode/:token/:clientId'
                            component={RedirectComponent}
                            isValidRoute={isApple || isStagingOrLower}
                            isCallBack={true}
                        />
                        {/* RENDERED ROUTES FOR NON-APPLE */}
                        <ProtectedRoute
                            exact
                            path='/plansearch'
                            component={PlanSearch}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/plansearch/:channelId/:clientId/:userId/:plannumber/:lastname'
                            component={PlanLookup}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/detailsoverview'
                            component={DetailsOverview}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/ReimbursementRequestInvoice/:clientId/:token'
                            component={DetailsOverview}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/planoverview'
                            component={PlanOverview}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/planoverview/contractsoverview'
                            component={ContractsOverview}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/contractoverview'
                            component={ViewPlanDetails}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/fileclaim/confirmation'
                            component={Confirmation}
                            history={history}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/fileclaim/payment'
                            component={Payment}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/fileclaim/verifyinfo'
                            component={VerifyInfo}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/fileclaim/reimbursement'
                            component={Reimbursement}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/coveragedocs'
                            component={CoverageDocsController}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/assetcoverageoverview'
                            component={AssetCoverageOverview}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/programcoveragedocs'
                            component={PlanType}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/vendor/:clientId/contactconfirmation'
                            component={ETContactConfirmation}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/accounts'
                            component={ViewAllAccounts}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/forgotplan'
                            component={ForgotPlanNumber}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/accountverification'
                            component={AccountVerification}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/createnewpassword'
                            component={CreateNewPassword}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/forgotpassword'
                            component={ForgotPassword}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/customerDetails'
                            component={CustomerDetails}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/altServiceoptions'
                            component={AltServiceOptions}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/faq'
                            component={FAQ}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        <ProtectedRoute
                            exact
                            path='/customerinfo'
                            component={CustomerInfo}
                            isValidRoute={!isApple || isStagingOrLower}
                        />
                        {/* THESE ARE ROUTES USED BY APPLE AND NON-APPLE - NO NEED FOR PROTECTED ROUTE*/}
                        <Route
                            exact
                            path='/vendor/apple/contactus'
                            component={ContactUs}
                        />
                        <Route
                            exact
                            path='/chat'
                            render={() => {
                                window.location.href = 'Mobile VA.html';
                            }}
                        />
                        <Route
                            exact
                            path='/chatend'
                            component={ChatEnd}
                        />
                        <Route
                            exact
                            path='/vendor/VerifyAigToken'
                            component={VerifyAigToken}
                        />
                        <Route
                            exact
                            path='/vendor/handshake'
                            component={PlanSearch}
                        />
                        <Route
                            exact
                            path='/coveragedocs/plantype'
                            component={PlanType}
                        />
                        <Route
                            exact
                            path='/info/contact-us'
                            component={ContactUs}
                        />
                        <Route
                            exact
                            path='/system-error'
                            component={SystemErrorMessage}
                        />
                        <Route
                            exact
                            path='/maintenance'
                            component={MaintenancePage}
                        />
                        <Route
                            exact
                            path='/documents/:name'
                            component={PdfViewer}
                        />
                        <Route
                            exact
                            path='/reimbursementform'
                            component={ReimbursementForm}
                        />
                        <Route
                            exact
                            path='/RequestReimbursementForm/:clientId/:token'
                            component={ReimbursementForm}
                        />
                        <Route
                            exact
                            path='/reimbursementconfirmation'
                            component={ReimbursementConfirmation}
                        />
                        <Route
                            exact
                            path='/ReimbursementRequestInvoice/:clientId/:token'
                            component={DetailsOverview}
                        />
                    </Switch>
                    <HelmetComponent />
                </main>

                {/* DISPLAY FOOTER IN CERTAIN SCENARIOS */}
                {!(
                    window.location.pathname.toLowerCase().includes('apple')
                    || window.location.pathname.toLowerCase().includes('file/payment')
                    || window.location.pathname.toLowerCase().includes('file/entitlement')
                    || window.location.pathname.toLowerCase().includes('fileclaim/verifyinfo')
                    || window.location.pathname.toLowerCase().includes('econsent')
                    || window.location.pathname.toLowerCase().includes('programsummarydisclosure')
                    || window.location.href.toLowerCase().includes('aigtheftandloss')
                    || window.location.href.toLowerCase().includes('serviceplans')
                    || window.location.pathname.toLowerCase().includes('planoverview')
                    || window.location.pathname.toLowerCase().includes('fileclaim/entitlement')
                    || window.location.pathname.toLowerCase().includes('appointment')
                    || window.location.pathname.toLowerCase().includes('fileclaim/confirmation')
                    || window.location.pathname.toLowerCase().includes('detailsoverview')
                    || window.location.pathname.toLowerCase().includes('contractoverview')
                    || window.location.pathname.toLowerCase().includes('chat')
                    || window.location.pathname.toLowerCase().includes('faq')
                    || window.location.pathname.toLowerCase().includes('coveragedocs')
                    || window.location.pathname.toLowerCase().includes('contact-us')
                    || window.location.pathname.toLowerCase().includes('info/contact-us')
                    || window.location.href.toLowerCase().includes('theftandloss')
                    || window.location.href.toLowerCase().includes('constellation')
                    || window.location.href.toLowerCase().includes('coveragedocscostco')
                    || window.location.href.toLowerCase().includes('aigdeviceprotect')
                    || window.location.href.toLowerCase().includes('rate')
                    || window.location.href.toLowerCase().includes('sure.homewarrantyplan')
                ) ? null : (
                    <FooterController />
                )}
                {isStagingOrLower && showBrandButtons && <DevelopmentBrandToggler />}
            </Providers>
        </IdleTimerProvider>
    );
}

// ALLOWS HELMET TO ACCESS ContractContext
const HelmetComponent = () => {
    const context = useContext(ContractContext);
    const idleTimer = useIdleTimerContext();

    // condition activates timeout modal
    if (context?.data?.loggedIn) {
        idleTimer.start();
    } else {
        idleTimer.reset();
        idleTimer.pause();
    }

    if (isAppleRoute()) {
        return (
            <Helmet>
                <link
                    rel='icon'
                    href={AIGFavicon}
                />
            </Helmet>
        );
    }
    if (!isAppleRoute()) {
        return (
            <Helmet>
                {/* Non-Apple/Non-Android Icons */}
                <link
                    rel='icon'
                    href={WISHFavicon}
                />
                <link
                    rel='icon'
                    sizes='16x16'
                    href={WISHFavicon16}
                />
                <link
                    rel='icon'
                    sizes='32x32'
                    href={WISHFavicon32}
                />
                {/* Android Icons */}
                <link
                    rel='icon'
                    type='image/png'
                    sizes='192x192'
                    href={WISHAndroidFavicon192}
                />
                <link
                    rel='icon'
                    type='image/png'
                    sizes='512x512'
                    href={WISHAndroidFavicon512}
                />
                {/* iOS Apple Touch */}
                <link
                    rel='apple-touch-icon'
                    sizes='180x180'
                    href={WISHAppleTouchFavicon}
                />
            </Helmet>
        );
    }
    if (!context.data)
        return (
            <Helmet>
                <link
                    rel='icon'
                    href=''
                />
                <title></title>
            </Helmet>
        );

    const { clientId } = context.data.contract || context;

    return (
        <Helmet>
            <meta charSet='utf-8' />
            {/* <meta name="description" content="this is react-helmet tutorial" /> */}
            <meta
                name='viewport'
                content='width=device-width, initial-scale=1.0'
            />
            <link
                rel='icon'
                href={getMetaDataByClient(clientId).favicon}
            />
            <title>{getMetaDataByClient(clientId?.toLowerCase).title}</title>
        </Helmet>
    );
};

// Can take a client ID and a page and return a full title, IE "AIG Claims | Plan Overview"
export const getMetaDataByClient = (clientId, append) => {
    const concatenated = (titleString) => {
        if (!append) return titleString;
        return titleString.concat(' | ', append);
    };

    const defaultMetaData = {
        favicon: WISHFavicon,
        title: concatenated('AIG Claims Portal')
    };

    if (isAppleRoute())
        return {
            favicon: AIGFavicon,
            title: ''
        };

    switch (clientId) {
        case 'hwa':
            return {
                favicon: HWAFavicon,
                title: concatenated('HWA Claims Portal')
            };
        case 'et':
            return {
                favicon: ETFavicon /* TODO: need to pull the actual ETFavicon (not provided yet)*/,
                title: concatenated('Energy Texas Claims Portal')
            };
        default:
            return defaultMetaData;
    }
};
